/**
 * /admin/data-uploads — load a depletions file without a terminal.
 *
 * VIP prohibits automated access to iDIG, so the export stays manual.
 * This page replaces everything after the download:
 *
 *   1. Upload   — drop the .xlsx. The API reads it and compares it with
 *                 what's loaded. Nothing is written.
 *   2. Review   — what the file is, what it would change, and every check
 *                 (ok / warning / blocked). A blocked file can't go on.
 *   3. Load     — only the uploader, within the review window, after
 *                 ticking "I've reviewed this". A Cloud Run job loads DEV
 *                 (and PROD, on the PROD dashboard) all or nothing; this
 *                 page follows its progress.
 *
 * Needs the `data_upload` role itself — being an admin is not enough.
 * Every step lands in the Audit Log under "Data uploads".
 */

import BlockIcon from '@mui/icons-material/Block';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react';

import { ApiError } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import {
  ACTIVE_STATUSES,
  useAddNoticeRecipient,
  useConfirmUpload,
  useDataUploads,
  useDiscardUpload,
  useNoticeRecipients,
  useRemoveNoticeRecipient,
  useUploadFile,
  type CheckLevel,
  type DataUpload,
  type MonthChange,
  type UploadStatus,
} from '../../api/dataUploads';
import { colors, uiType } from '../../theme';
import { VipExportGuide } from './VipExportGuide';

const INK = '#0f0a07';
const IVORY = '#fdf9f0';
const GOLD = colors.goldDark;
const RULE = 'rgba(15, 10, 7, 0.08)';
const MUTED = 'rgba(15, 10, 7, 0.6)';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';
const NUM = { fontVariantNumeric: 'tabular-nums' } as const;

// ── Formatting ───────────────────────────────────────────────────

/** `2026-10-01` → `Oct 2026`. */
function monthLabel(iso: string): string {
  const [y = 0, m = 1] = iso.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

/** `2026-10-12` → `Oct 12, 2026`. Date-only, so no timezone shift. */
function dayLabel(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y = 0, m = 1, d = 1] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function whenLabel(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function signed(n: number, digits = 1): string {
  const v = n.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return n > 0 ? `+${v}` : v;
}

/** A before → after change in plain words: `+544`, `-3.2` or `no change`. */
function change(before: number, after: number, digits = 1): string {
  const d = after - before;
  return Math.abs(d) < 0.5 * 10 ** -digits ? 'no change' : signed(d, digits);
}

/** Why a file was rejected, in the uploader's words — keyed by the blocking check. */
function rejectionReason(label: string, detail: string): string {
  switch (label) {
    case 'Recognised report':
      if (detail.startsWith('Only Excel')) return 'Not an Excel file';
      if (detail.includes('missing')) return 'Missing columns';
      return 'Not a VIP or Nevada report';
    case 'Not loaded before':
      return 'Already loaded';
    case 'Full history':
      return "Doesn't start Jan 2024";
    case 'Latest month':
      return "Last month doesn't match its date";
    case "Newer than what's loaded":
      return "Older than what's loaded";
    case 'Row count':
      return 'Too few rows';
    case 'Accounts recognised':
      return 'Accounts not recognised';
    case 'Has cases':
      return 'No cases in the file';
    default:
      return label;
  }
}

/** The loader's per-database status, in plain words. */
const TARGET_STATUS: Record<string, string> = {
  committed: 'saved',
  up_to_date: 'already up to date',
  rolled_back: 'rolled back — nothing saved',
  commit_failed: 'failed to save',
  loaded: 'loaded, not saved',
  not_reached: 'not started',
};

function targetsLabel(targets: string[]): string {
  return targets.map((t) => t.toUpperCase()).join(' + ');
}

function useNow(everyMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), everyMs);
    return () => window.clearInterval(id);
  }, [everyMs]);
  return now;
}

// ── Status vocabulary ────────────────────────────────────────────

type Tone = 'success' | 'warning' | 'error' | 'info' | 'muted';

const STATUS: Record<UploadStatus, { label: string; tone: Tone }> = {
  validated: { label: 'Awaiting confirmation', tone: 'info' },
  rejected: { label: 'Rejected', tone: 'error' },
  queued: { label: 'Starting', tone: 'info' },
  running: { label: 'Loading', tone: 'info' },
  committed: { label: 'Loaded', tone: 'success' },
  up_to_date: { label: 'Already loaded', tone: 'muted' },
  rolled_back: { label: 'Rolled back', tone: 'warning' },
  partial: { label: 'Partly loaded', tone: 'error' },
  failed: { label: 'Failed', tone: 'error' },
  discarded: { label: 'Discarded', tone: 'muted' },
  expired: { label: 'Expired', tone: 'muted' },
};

const TONE_COLOR: Record<Tone, string> = {
  success: colors.success,
  warning: colors.warning,
  error: colors.error,
  info: GOLD,
  muted: colors.textMuted,
};

function StatusPill({ status }: { status: UploadStatus }) {
  const { label, tone } = STATUS[status];
  const c = TONE_COLOR[tone];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.3,
        borderRadius: 999,
        fontFamily: FONT_INTER,
        fontSize: uiType.micro,
        fontWeight: 800,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        color: c,
        bgcolor: `${c}14`,
        border: `1px solid ${c}3d`,
      }}
    >
      {label}
    </Box>
  );
}

// Icons carry the meaning as well as colour, so the checks read the same
// for colour-blind users.
const CHECK_ICON: Record<CheckLevel, { Icon: typeof BlockIcon; color: string; label: string }> = {
  ok: { Icon: CheckCircleRoundedIcon, color: colors.success, label: 'Passed' },
  warn: { Icon: WarningAmberRoundedIcon, color: colors.warning, label: 'Warning' },
  block: { Icon: BlockIcon, color: colors.error, label: 'Blocked' },
};

// ── Building blocks ──────────────────────────────────────────────

function Card({ children, accent }: { children: ReactNode; accent?: string }) {
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: `1px solid ${RULE}`,
        borderRadius: 2,
        borderTop: accent ? `3px solid ${accent}` : undefined,
        p: { xs: 2, md: 3 },
        minWidth: 0,
      }}
    >
      {children}
    </Box>
  );
}

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <Typography
      sx={{
        fontFamily: FONT_INTER,
        fontSize: uiType.micro,
        fontWeight: 800,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        color: GOLD,
        mb: 1,
      }}
    >
      {children}
    </Typography>
  );
}

function Stat({
  label,
  value,
  hint,
  wide,
}: {
  label: string;
  value: string;
  hint?: string;
  /** Takes the full row on a phone (e.g. a date range). */
  wide?: boolean;
}) {
  return (
    <Box sx={{ minWidth: 0, gridColumn: wide ? { xs: '1 / -1', sm: 'auto' } : undefined }}>
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: uiType.micro,
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: MUTED,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{ fontFamily: FONT_INTER, fontSize: 18, fontWeight: 700, color: INK, mt: 0.25, ...NUM }}
      >
        {value}
      </Typography>
      {hint && (
        <Typography sx={{ fontFamily: FONT_INTER, fontSize: uiType.caption, color: MUTED }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

/** Cases with a fixed number of decimals, so a column's digits line up. */
function cases(n: number, digits: number): string {
  return n.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/**
 * Month-by-month 9L cases. `beforeLabel` is "Now" while the file is under
 * review and "Before" once it has loaded.
 */
function MonthTable({
  rows,
  caption,
  beforeLabel,
}: {
  rows: MonthChange[];
  caption: string;
  beforeLabel: 'Now' | 'Before';
}) {
  if (!rows.length) return null;
  const head = { fontWeight: 700, color: MUTED, fontSize: uiType.caption, py: 0.75, px: 1.25 };
  const cell = { fontSize: uiType.bodySm, py: 0.75, px: 1.25, borderTop: `1px solid ${RULE}` };
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box
        component="table"
        sx={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT_INTER, ...NUM }}
      >
        <caption style={{ captionSide: 'bottom', textAlign: 'left' }}>
          <Typography
            component="span"
            sx={{ fontFamily: FONT_INTER, fontSize: uiType.caption, color: MUTED }}
          >
            {caption}
          </Typography>
        </caption>
        <thead>
          <tr>
            <Box component="th" sx={{ ...head, textAlign: 'left' }}>
              Month
            </Box>
            <Box component="th" sx={{ ...head, textAlign: 'right' }}>
              {beforeLabel}
            </Box>
            <Box component="th" sx={{ ...head, textAlign: 'right' }}>
              After
            </Box>
            <Box component="th" sx={{ ...head, textAlign: 'right' }}>
              Change
            </Box>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            // Two decimals when one wouldn't add up: 136.25 → 136.33 (+0.08)
            // reads "136.3 → 136.3 +0.1" at one, 338.17 → 338.42 (+0.25)
            // reads "338.2 → 338.4 +0.3".
            const tenths = (n: number) => Math.round(n * 10);
            const addsUp =
              tenths(r.before) !== tenths(r.after) &&
              tenths(r.before) + tenths(r.delta) === tenths(r.after);
            const digits = addsUp ? 1 : 2;
            return (
              <tr key={r.month}>
                <Box component="td" sx={{ ...cell, fontWeight: 600 }}>
                  {monthLabel(r.month)}
                </Box>
                <Box component="td" sx={{ ...cell, textAlign: 'right', color: MUTED }}>
                  {cases(r.before, digits)}
                </Box>
                <Box component="td" sx={{ ...cell, textAlign: 'right' }}>
                  {cases(r.after, digits)}
                </Box>
                <Box
                  component="td"
                  sx={{
                    ...cell,
                    textAlign: 'right',
                    fontWeight: 700,
                    color: r.delta >= 0 ? colors.success : colors.error,
                  }}
                >
                  {signed(r.delta, digits)}
                </Box>
              </tr>
            );
          })}
        </tbody>
      </Box>
    </Box>
  );
}

// ── No access ────────────────────────────────────────────────────

function NoAccess() {
  return (
    <Card>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <LockOutlinedIcon sx={{ color: GOLD, mt: 0.25 }} />
        <Box>
          <Typography sx={{ fontFamily: FONT_INTER, fontWeight: 700, fontSize: 16, color: INK }}>
            You need the Data Upload role
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: uiType.body,
              color: MUTED,
              mt: 0.5,
              maxWidth: 620,
            }}
          >
            Loading depletion files changes the data everyone sees, so it isn't included with the
            Administrator role. Ask an administrator to add <strong>Data Upload</strong> to your
            account in Users → Edit roles.
          </Typography>
        </Box>
      </Stack>
    </Card>
  );
}

// ── Upload ───────────────────────────────────────────────────────

function Dropzone({
  maxMb,
  disabled,
  disabledReason,
  busy,
  onFile,
}: {
  maxMb: number;
  disabled: boolean;
  disabledReason?: string;
  busy: boolean;
  onFile: (f: File) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const off = disabled || busy;

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    if (off) return;
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  };

  return (
    <Box
      onDragOver={(e) => {
        e.preventDefault();
        if (!off) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      sx={{
        border: `1.5px dashed ${over ? GOLD : 'rgba(187, 140, 63, 0.45)'}`,
        bgcolor: over ? 'rgba(187, 140, 63, 0.08)' : IVORY,
        borderRadius: 2,
        px: { xs: 2, md: 4 },
        py: { xs: 3, md: 4.5 },
        textAlign: 'center',
        transition: 'background-color 160ms ease, border-color 160ms ease',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <input
        ref={input}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = '';
        }}
      />
      {busy ? (
        <Stack alignItems="center" spacing={1.25}>
          <CircularProgress size={28} sx={{ color: GOLD }} />
          <Typography sx={{ fontFamily: FONT_INTER, fontWeight: 700, color: INK }}>
            Checking the file…
          </Typography>
          <Typography sx={{ fontFamily: FONT_INTER, fontSize: uiType.bodySm, color: MUTED }}>
            Reading every row and comparing it with what's loaded. About 20 seconds. Nothing is
            loaded yet.
          </Typography>
        </Stack>
      ) : (
        <Stack alignItems="center" spacing={1.25}>
          <CloudUploadOutlinedIcon sx={{ fontSize: 34, color: GOLD }} />
          <Typography sx={{ fontFamily: FONT_INTER, fontWeight: 700, fontSize: 16, color: INK }}>
            Drop the depletions report here
          </Typography>
          <Typography sx={{ fontFamily: FONT_INTER, fontSize: uiType.bodySm, color: MUTED }}>
            VIP iDIG Rolling Periods export or Nevada distributor report · .xlsx · up to {maxMb} MB
          </Typography>
          <Button
            variant="contained"
            disableElevation
            disabled={off}
            onClick={() => input.current?.click()}
            sx={{
              mt: 0.5,
              bgcolor: INK,
              color: '#fff',
              fontFamily: FONT_INTER,
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              '&:hover': { bgcolor: '#2a211b' },
            }}
          >
            Choose file
          </Button>
          {disabled && disabledReason && (
            <Typography sx={{ fontFamily: FONT_INTER, fontSize: uiType.caption, color: MUTED }}>
              {disabledReason}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}

// ── Review ───────────────────────────────────────────────────────

function checkSummary(upload: DataUpload): string {
  const checks = upload.preview.checks;
  const blocked = checks.filter((c) => c.level === 'block').length;
  const warns = checks.filter((c) => c.level === 'warn').length;
  if (!blocked && !warns) return `All ${checks.length} checks passed`;
  return [
    blocked && `${blocked} blocked`,
    warns && `${warns} warning${warns === 1 ? '' : 's'}`,
    `${checks.length - blocked - warns} passed`,
  ]
    .filter(Boolean)
    .join(' · ');
}

function Checks({ upload, columns }: { upload: DataUpload; columns: number }) {
  // Problems first: what stops or needs a look should be read before what passed.
  const order: Record<CheckLevel, number> = { block: 0, warn: 1, ok: 2 };
  const checks = [...upload.preview.checks].sort((a, b) => order[a.level] - order[b.level]);
  return (
    <Box
      component="ul"
      sx={{
        listStyle: 'none',
        p: 0,
        m: 0,
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: `repeat(${Math.min(columns, 2)}, minmax(0, 1fr))`,
          lg: `repeat(${columns}, minmax(0, 1fr))`,
        },
        columnGap: 3,
        rowGap: 1.25,
      }}
    >
      {checks.map((c) => {
        const { Icon, color, label } = CHECK_ICON[c.level];
        return (
          <Box
            component="li"
            key={`${c.label}-${c.detail}`}
            sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', minWidth: 0 }}
          >
            <Icon aria-label={label} sx={{ color, fontSize: 20, mt: '1px', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{ fontFamily: FONT_INTER, fontSize: uiType.body, fontWeight: 700, color: INK }}
              >
                {c.label}
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: uiType.bodySm,
                  color: c.level === 'ok' ? MUTED : colors.textSecondary,
                  overflowWrap: 'anywhere',
                }}
              >
                {c.detail}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function ReviewCard({
  upload,
  onConfirm,
  onDiscard,
  discarding,
  loadRunning,
}: {
  upload: DataUpload;
  onConfirm: () => void;
  onDiscard: () => void;
  discarding: boolean;
  loadRunning: boolean;
}) {
  const now = useNow(15_000);
  const p = upload.preview;
  const minutesLeft = Math.max(
    0,
    Math.ceil((new Date(upload.expires_at).getTime() - now) / 60_000),
  );
  const blocked = p.blocked;
  const changes = p.month_changes ?? [];
  const accent = blocked ? colors.error : p.warnings ? colors.warning : colors.success;

  return (
    <Card accent={accent}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <DescriptionOutlinedIcon sx={{ color: GOLD }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontWeight: 700,
                fontSize: 16,
                color: INK,
                overflowWrap: 'anywhere',
              }}
            >
              {upload.filename}
            </Typography>
            <Typography sx={{ fontFamily: FONT_INTER, fontSize: uiType.caption, color: MUTED }}>
              {p.source_label ?? 'Unrecognised file'} · {(upload.size_bytes / 1024).toFixed(0)} KB ·
              uploaded {whenLabel(upload.created_at)}
              {upload.created_by_name ? ` by ${upload.created_by_name}` : ''}
            </Typography>
          </Box>
        </Stack>
        <StatusPill status={upload.status} />
      </Stack>

      {blocked ? (
        <Alert severity="error" sx={{ mt: 2, fontFamily: FONT_INTER }}>
          This file can't be loaded — see the blocked checks below. Nothing was changed.
        </Alert>
      ) : (
        <Typography sx={{ fontFamily: FONT_INTER, fontSize: uiType.body, color: MUTED, mt: 2 }}>
          Checked against what's loaded now. Nothing has been loaded yet.
        </Typography>
      )}

      {p.facts !== undefined && (
        <Box
          sx={{
            mt: 2.5,
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(3, minmax(0, 1fr))',
              lg: 'repeat(5, minmax(0, 1fr))',
            },
            gap: 2,
          }}
        >
          <Stat
            wide
            label="Covers"
            value={
              p.first_month
                ? `${monthLabel(p.first_month)} – ${monthLabel(p.last_month ?? p.first_month)}`
                : '—'
            }
          />
          <Stat label="Data through" value={dayLabel(p.data_through)} />
          <Stat label="Rows" value={(p.facts ?? 0).toLocaleString()} />
          <Stat
            label="Accounts"
            value={(p.accounts ?? 0).toLocaleString()}
            hint={p.new_accounts ? `${p.new_accounts.toLocaleString()} new` : 'none new'}
          />
          <Stat label="9L cases" value={cases(p.total_9l ?? 0, 1)} />
        </Box>
      )}

      <Box
        sx={{
          mt: 3,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: changes.length ? '1fr 1fr' : '1fr' },
          gap: 3,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Eyebrow>Checks · {checkSummary(upload)}</Eyebrow>
          <Checks upload={upload} columns={changes.length ? 1 : 3} />
        </Box>
        {changes.length > 0 && (
          <Box sx={{ minWidth: 0 }}>
            <Eyebrow>What changes</Eyebrow>
            <MonthTable
              rows={changes}
              beforeLabel="Now"
              caption="9L cases per month, all sources, if this file were loaded now. VIP usually revises the last few months."
            />
          </Box>
        )}
        {!blocked && p.facts !== undefined && changes.length === 0 && (
          <Typography sx={{ fontFamily: FONT_INTER, fontSize: uiType.bodySm, color: MUTED }}>
            No month totals would change — every row matches what's already loaded.
          </Typography>
        )}
      </Box>

      {upload.status === 'validated' && (
        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${RULE}` }}
        >
          <Typography sx={{ fontFamily: FONT_INTER, fontSize: uiType.caption, color: MUTED }}>
            {upload.can_confirm
              ? `Review expires in ${minutesLeft} min. Loads into ${targetsLabel(upload.targets)}.`
              : `Only ${upload.created_by_name ?? 'the uploader'} can load this file.`}
          </Typography>
          <Stack
            direction="row"
            spacing={1.25}
            justifyContent="flex-end"
            sx={{ '& > button': { flex: { xs: 1, sm: 'none' } } }}
          >
            <Button
              onClick={onDiscard}
              disabled={discarding}
              sx={{ fontFamily: FONT_INTER, fontWeight: 600, textTransform: 'none', color: MUTED }}
            >
              Discard
            </Button>
            {upload.can_confirm && (
              <Button
                variant="contained"
                disableElevation
                disabled={loadRunning}
                onClick={onConfirm}
                sx={{
                  bgcolor: GOLD,
                  color: '#fff',
                  fontFamily: FONT_INTER,
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 1.5,
                  px: 2.5,
                  '&:hover': { bgcolor: '#a27832' },
                }}
              >
                Load data…
              </Button>
            )}
          </Stack>
        </Stack>
      )}
    </Card>
  );
}

function ConfirmDialog({
  upload,
  open,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  upload: DataUpload;
  open: boolean;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const [reviewed, setReviewed] = useState(false);
  useEffect(() => {
    if (open) setReviewed(false);
  }, [open]);
  const p = upload.preview;
  const targets = targetsLabel(upload.targets);
  // Full screen on a phone: a centred dialog leaves no room for the checkbox.
  const fullScreen = useMediaQuery(useTheme().breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { bgcolor: IVORY, borderTop: `3px solid ${GOLD}` } }}
    >
      <DialogTitle sx={{ fontFamily: FONT_INTER, fontWeight: 700, color: INK }}>
        Load into {targets}?
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontFamily: FONT_INTER, fontSize: uiType.body, color: INK }}>
          <strong>{upload.filename}</strong> — {p.source_label}, data through{' '}
          {dayLabel(p.data_through)}.
        </Typography>
        <Box
          component="ul"
          sx={{ fontFamily: FONT_INTER, fontSize: uiType.bodySm, color: MUTED, pl: 2.5 }}
        >
          <li>
            {upload.targets.length > 1
              ? `Loads into ${targets} together. If any check fails during the load, both are rolled back and nothing changes.`
              : `Loads into ${targets}. If any check fails during the load, it's rolled back and nothing changes.`}
          </li>
          <li>
            Takes a few minutes. You can leave this page — a confirmation email is sent when it's
            done.
          </li>
          <li>The file is deleted once it's loaded.</li>
        </Box>
        {p.warnings > 0 && (
          <Alert severity="warning" sx={{ fontFamily: FONT_INTER, mb: 1.5 }}>
            {p.warnings} warning{p.warnings === 1 ? '' : 's'} to review — see the checks on the
            page.
          </Alert>
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={reviewed}
              onChange={(e) => setReviewed(e.target.checked)}
              sx={{ color: GOLD, '&.Mui-checked': { color: GOLD } }}
            />
          }
          label={
            <Typography sx={{ fontFamily: FONT_INTER, fontSize: uiType.body, color: INK }}>
              I've reviewed this file{p.warnings > 0 ? ' and its warnings' : ''} and want to load
              it.
            </Typography>
          }
        />
        {error && (
          <Alert severity="error" sx={{ fontFamily: FONT_INTER, mt: 1 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          gap: 1,
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          alignItems: 'stretch',
          '& > :not(style) ~ :not(style)': { ml: { xs: 0, sm: 1 } },
        }}
      >
        <Button
          onClick={onClose}
          disabled={busy}
          sx={{ fontFamily: FONT_INTER, fontWeight: 600, textTransform: 'none', color: MUTED }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={!reviewed || busy}
          onClick={onConfirm}
          sx={{
            bgcolor: GOLD,
            color: '#fff',
            fontFamily: FONT_INTER,
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: 1.5,
            '&:hover': { bgcolor: '#a27832' },
          }}
        >
          {busy ? 'Starting…' : 'Load data'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Progress ─────────────────────────────────────────────────────

function stagesFor(targets: string[]): string[] {
  return ['Check file', ...targets.map((t) => `Load ${t.toUpperCase()}`), 'Save'];
}

function stageIndex(step: string, targets: string[]): number {
  if (step.startsWith('commit')) return targets.length + 1;
  const i = targets.findIndex((t) => step.toUpperCase().includes(t.toUpperCase()));
  return i >= 0 ? i + 1 : 0;
}

function ProgressCard({ upload }: { upload: DataUpload }) {
  const now = useNow(1_000);
  const stages = stagesFor(upload.targets);
  const prog = upload.progress ?? { step: 'starting', done: 0, total: 0 };
  const stage = upload.status === 'queued' ? 0 : stageIndex(prog.step, upload.targets);
  const within = prog.total ? prog.done / prog.total : 0;
  // Each database load is most of the work; checking and saving are quick.
  const per = 90 / Math.max(upload.targets.length, 1);
  const pct =
    stage === 0 ? 3 : stage <= upload.targets.length ? 5 + per * (stage - 1) + per * within : 97;
  const started = upload.started_at ?? upload.confirmed_at ?? upload.created_at;
  const secs = Math.max(0, Math.round((now - new Date(started).getTime()) / 1000));

  return (
    <Card accent={GOLD}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={1}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Eyebrow>Load in progress</Eyebrow>
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontWeight: 700,
              fontSize: 16,
              color: INK,
              overflowWrap: 'anywhere',
            }}
          >
            {upload.filename} → {targetsLabel(upload.targets)}
          </Typography>
          <Typography sx={{ fontFamily: FONT_INTER, fontSize: uiType.caption, color: MUTED }}>
            Started by {upload.confirmed_by_name ?? 'someone'} · {Math.floor(secs / 60)}m{' '}
            {String(secs % 60).padStart(2, '0')}s
          </Typography>
        </Box>
        <StatusPill status={upload.status} />
      </Stack>

      <LinearProgress
        variant="determinate"
        value={pct}
        aria-label="Load progress"
        sx={{
          mt: 2.5,
          height: 8,
          borderRadius: 999,
          bgcolor: 'rgba(187, 140, 63, 0.15)',
          '& .MuiLinearProgress-bar': { bgcolor: GOLD, borderRadius: 999 },
        }}
      />
      <Box
        component="ol"
        sx={{
          listStyle: 'none',
          p: 0,
          m: 0,
          mt: 1.5,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', sm: `repeat(${stages.length}, 1fr)` },
          gap: 1,
        }}
      >
        {stages.map((s, i) => {
          const done = i < stage;
          const current = i === stage;
          return (
            <Box
              component="li"
              key={s}
              sx={{
                fontFamily: FONT_INTER,
                fontSize: uiType.caption,
                fontWeight: current ? 700 : 500,
                color: done ? colors.success : current ? INK : MUTED,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              {done ? (
                <CheckCircleRoundedIcon sx={{ fontSize: 15 }} aria-label="Done" />
              ) : current ? (
                <CircularProgress size={11} thickness={6} sx={{ color: GOLD }} />
              ) : null}
              {s}
              {current && prog.total > 0 && stage <= upload.targets.length && (
                <Box component="span" sx={{ color: MUTED, fontWeight: 500, ...NUM }}>
                  {' '}
                  {Math.round(within * 100)}%
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
      <Typography sx={{ fontFamily: FONT_INTER, fontSize: uiType.caption, color: MUTED, mt: 2 }}>
        Nothing is visible on the dashboard until every database has loaded and passed its checks.
        You can leave this page — a confirmation email is sent when it finishes.
      </Typography>
    </Card>
  );
}

// ── Outcome ──────────────────────────────────────────────────────

const OUTCOME_COPY: Partial<Record<UploadStatus, string>> = {
  committed: 'Loaded and saved.',
  up_to_date: 'This file was already loaded. Nothing changed.',
  rolled_back:
    'Issues were found during the load, so every database was rolled back. Nothing was saved.',
  partial: 'One database saved and another did not. Run the same file again to finish the other.',
  failed: 'The load stopped before saving anything.',
};

function ResultCard({ upload }: { upload: DataUpload }) {
  const r = upload.result;
  const ok = upload.status === 'committed' || upload.status === 'up_to_date';
  const issues = r?.issues ?? [];
  const targets = r?.targets?.filter((t) => t.before && t.after) ?? [];
  const months = targets[0]?.month_changes ?? [];

  return (
    <Card accent={ok ? colors.success : colors.error}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={1}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Eyebrow>Load result</Eyebrow>
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontWeight: 700,
              fontSize: 16,
              color: INK,
              overflowWrap: 'anywhere',
            }}
          >
            {upload.filename} → {targetsLabel(upload.targets)}
          </Typography>
          <Typography sx={{ fontFamily: FONT_INTER, fontSize: uiType.caption, color: MUTED }}>
            {upload.finished_at ? `Finished ${whenLabel(upload.finished_at)}` : ''}
            {upload.confirmed_by_name ? ` · started by ${upload.confirmed_by_name}` : ''}
          </Typography>
        </Box>
        <StatusPill status={upload.status} />
      </Stack>

      <Alert severity={ok ? 'success' : 'error'} sx={{ mt: 2, fontFamily: FONT_INTER }}>
        {OUTCOME_COPY[upload.status] ?? STATUS[upload.status].label}
        {r?.error && (
          <Box component="span" sx={{ display: 'block', mt: 0.5, overflowWrap: 'anywhere' }}>
            {r.error}
          </Box>
        )}
      </Alert>
      {issues.length > 0 && (
        <Box
          component="ul"
          sx={{ fontFamily: FONT_INTER, fontSize: uiType.bodySm, color: INK, pl: 2.5 }}
        >
          {issues.map((i) => (
            <li key={i}>{i}</li>
          ))}
        </Box>
      )}

      {targets.length > 0 && (
        <Box
          sx={{
            mt: 2.5,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: months.length ? '1fr 1fr' : '1fr' },
            gap: 3,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Eyebrow>Database changes</Eyebrow>
            <Stack spacing={1.5}>
              {targets.map((t) => (
                <Box key={t.name}>
                  <Typography
                    sx={{
                      fontFamily: FONT_INTER,
                      fontWeight: 700,
                      fontSize: uiType.body,
                      color: INK,
                    }}
                  >
                    {t.name.toUpperCase()} ·{' '}
                    {TARGET_STATUS[t.status] ?? t.status.replace(/_/g, ' ')}
                  </Typography>
                  <Typography
                    sx={{ fontFamily: FONT_INTER, fontSize: uiType.bodySm, color: MUTED, ...NUM }}
                  >
                    Rows {t.before!.facts.toLocaleString()} → {t.after!.facts.toLocaleString()} (
                    {change(t.before!.facts, t.after!.facts, 0)}) · Accounts{' '}
                    {change(t.before!.accounts, t.after!.accounts, 0)} · 9L cases{' '}
                    {cases(t.before!.total_9l, 1)} → {cases(t.after!.total_9l, 1)} (
                    {change(t.before!.total_9l, t.after!.total_9l)})
                  </Typography>
                  {t.load && (
                    <Typography
                      sx={{
                        fontFamily: FONT_INTER,
                        fontSize: uiType.caption,
                        color: MUTED,
                        ...NUM,
                      }}
                    >
                      {t.load.inserted.toLocaleString()} new · {t.load.updated.toLocaleString()}{' '}
                      updated · {t.load.unchanged.toLocaleString()} unchanged ·{' '}
                      {t.load.failed.toLocaleString()} failed
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
          {months.length > 0 && (
            <Box sx={{ minWidth: 0 }}>
              <Eyebrow>Months that moved</Eyebrow>
              <MonthTable
                rows={months}
                beforeLabel="Before"
                caption="9L cases per month, all sources."
              />
            </Box>
          )}
        </Box>
      )}
    </Card>
  );
}

// ── Who gets the email ───────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function RecipientRow({
  avatar,
  title,
  tag,
  meta,
  action,
}: {
  avatar: ReactNode;
  title: string;
  tag?: string;
  meta: string;
  action?: ReactNode;
}) {
  return (
    <Box
      component="li"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.25,
        minWidth: 0,
        '& .row-action': { opacity: { md: 0 }, transition: 'opacity 140ms ease' },
        '&:hover .row-action, & .row-action:focus-visible': { opacity: 1 },
      }}
    >
      {avatar}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack
          direction="row"
          useFlexGap
          flexWrap="wrap"
          columnGap={1}
          rowGap={0.25}
          alignItems="center"
          sx={{ minWidth: 0 }}
        >
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: uiType.body,
              fontWeight: 600,
              color: INK,
              overflowWrap: 'anywhere',
            }}
          >
            {title}
          </Typography>
          {tag && (
            <Box
              component="span"
              sx={{
                flexShrink: 0,
                px: 0.75,
                py: 0.1,
                borderRadius: 999,
                fontFamily: FONT_INTER,
                fontSize: uiType.micro,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: GOLD,
                bgcolor: 'rgba(187, 140, 63, 0.1)',
              }}
            >
              {tag}
            </Box>
          )}
        </Stack>
        <Typography sx={{ fontFamily: FONT_INTER, fontSize: uiType.caption, color: MUTED }}>
          {meta}
        </Typography>
      </Box>
      {action}
    </Box>
  );
}

function Initial({ email }: { email: string }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: 32,
        height: 32,
        flexShrink: 0,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        bgcolor: 'rgba(187, 140, 63, 0.12)',
        color: GOLD,
        fontFamily: FONT_INTER,
        fontWeight: 700,
        fontSize: uiType.bodySm,
        textTransform: 'uppercase',
      }}
    >
      {email.charAt(0)}
    </Box>
  );
}

function Recipients() {
  const { user } = useAuth();
  const list = useNoticeRecipients();
  const add = useAddNoticeRecipient();
  const remove = useRemoveNoticeRecipient();
  const [email, setEmail] = useState('');
  const items = list.data?.items ?? [];
  const me = user?.email?.toLowerCase();
  const meListed = !!me && items.some((r) => r.email === me);
  const valid = EMAIL_RE.test(email.trim());
  const error = add.error instanceof ApiError ? add.error.serverMessage : null;

  const submit = (value: string) =>
    add.mutate(value.trim().toLowerCase(), { onSuccess: () => setEmail('') });

  return (
    <Card>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 300px) minmax(0, 1fr)' },
          gap: { xs: 2, md: 4 },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <MailOutlineRoundedIcon sx={{ color: GOLD, fontSize: 20 }} />
            <Typography
              component="h3"
              sx={{ fontFamily: FONT_INTER, fontWeight: 700, fontSize: 16, color: INK }}
            >
              Email notifications
            </Typography>
          </Stack>
          <Typography sx={{ fontFamily: FONT_INTER, fontSize: uiType.bodySm, color: MUTED, mt: 1 }}>
            A short confirmation when a load finishes — whether it loaded, where, when and by whom.
            No data is included.
          </Typography>
          {me && !meListed && list.isSuccess && (
            <Button
              size="small"
              disabled={add.isPending}
              onClick={() => submit(me)}
              sx={{
                mt: 1.5,
                ml: -1,
                fontFamily: FONT_INTER,
                fontWeight: 700,
                textTransform: 'none',
                color: GOLD,
              }}
            >
              + Email me too
            </Button>
          )}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ border: `1px solid ${RULE}`, borderRadius: 2, overflow: 'hidden' }}>
            <Box
              sx={{
                px: 2,
                py: 1,
                bgcolor: IVORY,
                borderBottom: `1px solid ${RULE}`,
                fontFamily: FONT_INTER,
                fontSize: uiType.micro,
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: MUTED,
              }}
            >
              {list.isLoading
                ? 'Recipients'
                : `${items.length} recipient${items.length === 1 ? '' : 's'}`}
            </Box>
            {list.isLoading ? (
              <Skeleton variant="rectangular" height={56} />
            ) : (
              <Stack
                component="ul"
                divider={<Box component="li" aria-hidden sx={{ borderTop: `1px solid ${RULE}` }} />}
                sx={{ listStyle: 'none', p: 0, m: 0 }}
              >
                {items.map((r) => (
                  <RecipientRow
                    key={r.id}
                    avatar={<Initial email={r.email} />}
                    title={r.email}
                    tag={r.email === me ? 'You' : undefined}
                    meta={
                      r.added_by_name
                        ? `Added by ${r.added_by_name} · ${shortDate(r.added_at)}`
                        : `Added ${shortDate(r.added_at)}`
                    }
                    action={
                      <Tooltip title="Remove">
                        <IconButton
                          className="row-action"
                          size="small"
                          aria-label={`Stop emailing ${r.email}`}
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(r.id)}
                          sx={{ color: MUTED, '&:hover': { color: colors.error } }}
                        >
                          <CloseRoundedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    }
                  />
                ))}
                <RecipientRow
                  avatar={
                    <Box
                      aria-hidden
                      sx={{
                        width: 32,
                        height: 32,
                        flexShrink: 0,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        border: `1px dashed rgba(187, 140, 63, 0.5)`,
                        color: GOLD,
                      }}
                    >
                      <CloudUploadOutlinedIcon sx={{ fontSize: 16 }} />
                    </Box>
                  }
                  title="Whoever uploads the file"
                  tag="Always"
                  meta="Gets the email for their own load, listed or not."
                />
              </Stack>
            )}
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                if (valid) submit(email);
              }}
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                p: 1.5,
                bgcolor: IVORY,
                borderTop: `1px solid ${RULE}`,
              }}
            >
              <TextField
                size="small"
                type="email"
                placeholder="Add an email address"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (add.error) add.reset();
                }}
                inputProps={{ 'aria-label': 'Email address to add' }}
                InputProps={{
                  startAdornment: (
                    <MailOutlineRoundedIcon sx={{ fontSize: 18, color: MUTED, mr: 1 }} />
                  ),
                }}
                sx={{
                  flex: '1 1 220px',
                  bgcolor: '#fff',
                  '& input': { fontFamily: FONT_INTER },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                disableElevation
                disabled={!valid || add.isPending}
                sx={{
                  bgcolor: INK,
                  color: '#fff',
                  fontFamily: FONT_INTER,
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 1.5,
                  px: 2,
                  flex: { xs: '1 1 100%', sm: 'none' },
                  '&:hover': { bgcolor: '#2a211b' },
                }}
              >
                {add.isPending ? 'Adding…' : 'Add recipient'}
              </Button>
            </Box>
          </Box>
          {(error || remove.error) && (
            <Alert severity="error" sx={{ fontFamily: FONT_INTER, mt: 1.5 }}>
              {error ?? "Couldn't remove that address. Try again."}
            </Alert>
          )}
        </Box>
      </Box>
    </Card>
  );
}

// ── History ──────────────────────────────────────────────────────

function resultSummary(u: DataUpload): string {
  const t = u.result?.targets?.find((x) => x.before && x.after);
  if (u.status === 'committed' && t?.before && t.after) {
    const rows = t.after.facts - t.before.facts;
    const cases = t.after.total_9l - t.before.total_9l;
    if (!rows && Math.abs(cases) < 0.05) return 'No changes';
    return `${signed(rows, 0)} rows · ${signed(cases)} cases`;
  }
  if (u.status === 'rejected') {
    const block = u.preview.checks.find((c) => c.level === 'block');
    return block ? rejectionReason(block.label, block.detail) : 'Failed validation';
  }
  return u.preview.data_through ? `through ${dayLabel(u.preview.data_through)}` : '';
}

/**
 * Column tracks shared by the header and every row. Fixed widths, not
 * `auto`: each row is its own grid, so a content-sized column (the status
 * pill) would make every row's columns start at a different place.
 */
const HISTORY_COLUMNS = {
  xs: 'minmax(0, 1fr)',
  md: 'minmax(0, 1fr) 210px 190px',
  lg: '140px minmax(0, 1fr) 170px 210px 190px',
} as const;

function History({
  items,
  selectedId,
  onSelect,
}: {
  items: DataUpload[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}) {
  if (!items.length) {
    return (
      <Typography sx={{ fontFamily: FONT_INTER, fontSize: uiType.bodySm, color: MUTED }}>
        No uploads yet.
      </Typography>
    );
  }
  const headCell = {
    fontFamily: FONT_INTER,
    fontSize: uiType.micro,
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: MUTED,
  } as const;
  return (
    <Stack divider={<Box sx={{ borderTop: `1px solid ${RULE}` }} />}>
      <Box
        aria-hidden
        sx={{
          display: { xs: 'none', md: 'grid' },
          gridTemplateColumns: HISTORY_COLUMNS,
          columnGap: { md: 1.5, lg: 2 },
          px: 1.5,
          pb: 1,
        }}
      >
        <Box sx={{ ...headCell, display: { md: 'none', lg: 'block' } }}>When</Box>
        <Box sx={headCell}>File</Box>
        <Box sx={{ ...headCell, display: { md: 'none', lg: 'block' } }}>Uploaded by</Box>
        <Box sx={headCell}>Result</Box>
        <Box sx={{ ...headCell, textAlign: 'right' }}>Status</Box>
      </Box>
      {items.map((u) => (
        <Box
          key={u.id}
          component="button"
          type="button"
          onClick={() => onSelect(u.id)}
          aria-pressed={selectedId === u.id}
          sx={{
            all: 'unset',
            cursor: 'pointer',
            display: 'grid',
            // Phone: one column, status under the file. Tablet adds the result
            // beside it. Desktop: every column.
            gridTemplateColumns: HISTORY_COLUMNS,
            columnGap: { xs: 1.5, lg: 2 },
            rowGap: 0.5,
            alignItems: 'center',
            px: 1.5,
            py: 1.25,
            borderRadius: 1,
            bgcolor: selectedId === u.id ? 'rgba(187, 140, 63, 0.08)' : 'transparent',
            '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.06)' },
            '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: 1 },
            fontFamily: FONT_INTER,
            fontSize: uiType.bodySm,
            color: INK,
          }}
        >
          <Box sx={{ color: MUTED, ...NUM, display: { xs: 'none', lg: 'block' } }}>
            {whenLabel(u.created_at)}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{u.filename}</Box>
            <Box sx={{ color: MUTED, fontSize: uiType.caption, display: { lg: 'none' } }}>
              {whenLabel(u.created_at)} · {u.created_by_name ?? '—'}
            </Box>
            <Box sx={{ color: MUTED, fontSize: uiType.caption }}>
              {u.preview.source_label ?? 'Unrecognised'}
              <Box component="span" sx={{ display: { md: 'none' } }}>
                {resultSummary(u) ? ` · ${resultSummary(u)}` : ''}
              </Box>
            </Box>
          </Box>
          <Box sx={{ color: MUTED, display: { xs: 'none', lg: 'block' }, minWidth: 0 }}>
            {u.created_by_name ?? '—'}
          </Box>
          <Box sx={{ color: MUTED, ...NUM, display: { xs: 'none', md: 'block' }, minWidth: 0 }}>
            {resultSummary(u)}
          </Box>
          <Box sx={{ justifySelf: { xs: 'start', md: 'end' } }}>
            <StatusPill status={u.status} />
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

// ── Page ─────────────────────────────────────────────────────────

export function DataUploadsPage() {
  const list = useDataUploads();
  const upload = useUploadFile();
  const confirm = useConfirmUpload();
  const discard = useDiscardUpload();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const data = list.data;
  const active = data?.active ?? null;
  const items = useMemo(() => data?.items ?? [], [data]);

  // Show what you're working on: the chosen row, else your newest upload
  // still awaiting confirmation, else a load that finished in the last
  // hour — so coming back to the page shows how it ended.
  const selected = useMemo(() => {
    if (selectedId !== null) return items.find((u) => u.id === selectedId) ?? null;
    const pending = items.find((u) => u.status === 'validated' && u.can_confirm);
    if (pending) return pending;
    // A load in progress has its own card; an older result beside it misleads.
    if (active) return null;
    const latest = items.find((u) => u.result);
    const recent =
      latest?.finished_at && Date.now() - new Date(latest.finished_at).getTime() < 3_600_000;
    return recent ? latest : null;
  }, [items, selectedId, active]);

  const noAccess = list.error instanceof ApiError && list.error.status === 403;
  const targets = data?.targets ?? [];

  if (noAccess) return <NoAccess />;

  return (
    <Stack spacing={3} sx={{ pb: 6 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'flex-end' }}
      >
        <Box sx={{ maxWidth: 680 }}>
          <Typography
            component="h2"
            sx={{ fontFamily: FONT_INTER, fontWeight: 700, fontSize: uiType.title, color: INK }}
          >
            Load depletion data
          </Typography>
          <Typography sx={{ fontFamily: FONT_INTER, fontSize: uiType.body, color: MUTED, mt: 0.5 }}>
            Upload the report you exported from VIP. It's checked first — nothing is loaded until
            you review the result and confirm.
          </Typography>
        </Box>
        {targets.length > 0 && (
          <Tooltip
            arrow
            title={
              targets.includes('prod')
                ? 'Loads into PROD (live) and DEV (test) together, so both stay identical.'
                : 'Loads into DEV only. Use the live site to update the live dashboard.'
            }
          >
            <Box
              tabIndex={0}
              sx={{
                fontFamily: FONT_INTER,
                fontSize: uiType.caption,
                fontWeight: 700,
                color: INK,
                bgcolor: IVORY,
                border: `1px solid rgba(187, 140, 63, 0.35)`,
                borderRadius: 999,
                px: 1.5,
                py: 0.6,
                whiteSpace: { xs: 'normal', md: 'nowrap' },
                flexShrink: 0,
              }}
            >
              {targets.includes('prod')
                ? 'Updates the live dashboard'
                : "Test site only · the live dashboard isn't affected"}
            </Box>
          </Tooltip>
        )}
      </Stack>

      {list.isLoading ? (
        <Skeleton variant="rounded" height={180} />
      ) : list.isError ? (
        <Alert severity="error" sx={{ fontFamily: FONT_INTER }}>
          Couldn't load data uploads.{' '}
          {list.error instanceof ApiError ? list.error.serverMessage : ''}
        </Alert>
      ) : (
        <>
          {active && <ProgressCard upload={active} />}

          <VipExportGuide />

          <Dropzone
            maxMb={data?.max_mb ?? 10}
            disabled={!!active}
            disabledReason="A load is running. Upload the next file when it finishes."
            busy={upload.isPending}
            onFile={(f) =>
              upload.mutate(f, {
                onSuccess: (u) => setSelectedId(u.id),
              })
            }
          />
          {upload.error && (
            <Alert severity="error" sx={{ fontFamily: FONT_INTER }}>
              {upload.error instanceof ApiError ? upload.error.serverMessage : 'Upload failed.'}
            </Alert>
          )}

          {selected &&
            (selected.status === 'validated' ||
              selected.status === 'rejected' ||
              selected.status === 'expired' ||
              selected.status === 'discarded') && (
              <ReviewCard
                upload={selected}
                loadRunning={!!active}
                discarding={discard.isPending}
                onDiscard={() => discard.mutate(selected.id)}
                onConfirm={() => {
                  confirm.reset();
                  setConfirmOpen(true);
                }}
              />
            )}
          {selected && !ACTIVE_STATUSES.includes(selected.status) && selected.result && (
            <ResultCard upload={selected} />
          )}

          <Recipients />

          <Card>
            <Eyebrow>Recent uploads</Eyebrow>
            <History
              items={items}
              selectedId={selected?.id ?? null}
              onSelect={(id) => setSelectedId(id)}
            />
          </Card>

          {selected && selected.status === 'validated' && (
            <ConfirmDialog
              upload={selected}
              open={confirmOpen}
              busy={confirm.isPending}
              error={confirm.error instanceof ApiError ? confirm.error.serverMessage : null}
              onClose={() => setConfirmOpen(false)}
              onConfirm={() =>
                confirm.mutate(
                  { id: selected.id, acknowledgeWarnings: true },
                  {
                    onSuccess: (u) => {
                      setConfirmOpen(false);
                      setSelectedId(u.id);
                    },
                  },
                )
              }
            />
          )}
        </>
      )}
    </Stack>
  );
}
